import { memo } from "react";
import { Paper, Skeleton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { faCircleCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";

type ImportChecksCardProps = {
  isFullScreen?: boolean;
};

export const ImportChecksCard: React.FC<ImportChecksCardProps> = memo(({ isFullScreen }) => {
  const { selectedDate } = useShiftTimes();
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid;

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", "importChecks", selectedDate.toISODate()],
    queryFn: async () => {
      const res = await dashboardsAjax.getImportCheckCardData(selectedDate);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: queryEnabled,
  });

  const showSkeleton = isLoading || !queryEnabled;
  const entries = data?.entries ?? [];
  const isClean = !showSkeleton && !!data && entries.length === 0;
  const isUnavailable = !showSkeleton && !data;

  return (
    <Paper className="!shadow-none !border-none !w-full p-3 h-full relative overflow-y-auto !rounded-none !bg-theme-surface-2">
      {showSkeleton && (
        <div className="flex flex-col gap-2 animate-fade-in">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl px-3 py-3 bg-theme-table-heading">
              <Skeleton animation="wave" variant="text" width="30%" height={22} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="70%" height={18} className="bg-theme-table-row-even" />
            </div>
          ))}
        </div>
      )}

      {isUnavailable && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          icon={faTriangleExclamation}
          title={isOnline ? "No import data" : "Not available offline"}
          subtitle={isOnline
            ? "Nothing has been imported for the selected date."
            : "Import checks are calculated by the server, so they cannot be shown while offline."}
        />
      )}

      {isClean && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          icon={faCircleCheck}
          title="Imports look consistent"
          subtitle={`All ${data?.unitsChecked ?? 0} machines agree between the Operating Hours and Fleet Utilization files for this date.`}
        />
      )}

      {!showSkeleton && entries.length > 0 && (
        <div className="animate-fade-in flex flex-col gap-2">
          <Typography variant="body2" className="!text-theme-text-muted !mb-1">
            {entries.length} of {data?.unitsChecked ?? 0} machines need checking against the imported spreadsheets.
          </Typography>

          {entries.map(entry => (
            <div
              key={entry.plantRef}
              className="rounded-xl border border-theme-border bg-theme-surface px-3 py-2 flex items-start gap-3 transition hover:border-theme-table-heading-contrast"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-theme-warning mt-1" />
              <div className="text-left">
                <Typography variant="body2" className="!font-bold !text-theme-text">
                  {entry.plantRef} &mdash; {entry.issue}
                </Typography>
                <Typography variant="caption" className="!text-theme-text-muted">
                  {entry.detail}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
    </Paper>
  );
});
